import React, { useEffect, useMemo } from 'react';
import {
  FaTimes,
  FaArrowLeft,
  FaDirections,
  FaMapMarkerAlt,
  FaClock,
  FaRoute,
  FaCheckCircle,
  FaRegCircle,
  FaInfoCircle,
  FaExternalLinkAlt,
  FaBed,
  FaUtensils,
  FaStar,
  FaCompass,
  FaVolumeUp,
  FaTrash,
  FaLock,
  FaLockOpen
} from 'react-icons/fa';
import DayWeather from '../DayWeather';
import ReviewLinks from '../ReviewLinks';
import BookingOptions from '../BookingOptions';
import AffiliateDisclosure from '../AffiliateDisclosure';
import { formatDayDate, getDayDate, getAttractionKey, getDayWeather, getAttractionStatus } from './dashboardUtils';
import { buildGoogleMapsRouteUrl } from '../../utils/itineraryParser';
import { usePlaceImage, nightWindowForDay, dayHasAccommodation } from '@travel-planner/shared';
import { ReadAloudButton } from '../voice/TextToSpeech';
import { useVoiceSupport } from '../voice/VoiceProvider';
import PageGuide from '../PageGuide';

// Pattern to detect meal-related locations
const MEAL_PATTERN = /\b(breakfast|lunch|dinner|brunch|meal|restaurant|cafe|bistro|eatery|diner|food|dining)\b/i;

/**
 * DayStopCard - Individual stop card with place image support.
 * Extracted to enable usePlaceImage hook per stop.
 */
const DayStopCard = ({
  loc,
  idx,
  dayNumber,
  isVisited,
  isLocked,
  customTime,
  onAttractionStatusUpdate,
  destination,
  getMoreInfoUrl,
  getPlaceUrl,
  locations,
  onExplore,
  onClose,
  checkInDate,
  checkOutDate,
  onRemoveStop,
  isOwner,
  bookingSurface,
}) => {
  const imageUrl = usePlaceImage(loc.name, loc.lat, loc.lng);
  const [imageError, setImageError] = React.useState(false);
  const [removeState, setRemoveState] = React.useState('idle'); // 'idle' | 'removing' | 'queued'

  // Reset error state when URL changes
  React.useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  const handleRemove = async () => {
    if (!onRemoveStop || removeState !== 'idle') return;
    setRemoveState('removing');
    try {
      await onRemoveStop(dayNumber, loc.name, loc.lat, loc.lng);
      setRemoveState('queued');
    } catch (error) {
      setRemoveState('idle');
    }
  };

  // Placeholder component for when no image is available
  const ImagePlaceholder = () => (
    <div className={`
      w-[72px] h-[72px] rounded-lg flex-shrink-0 bg-gradient-to-br from-slate-100 to-slate-200
      flex items-center justify-center
    `}>
      <FaMapMarkerAlt className="text-slate-400 text-xl" />
    </div>
  );

  return (
    <div
      className={`
        rounded-xl border-2 transition-all shadow-sm
        ${isLocked
          ? 'bg-amber-50 border-amber-300'
          : isVisited
            ? 'bg-emerald-50 border-emerald-300'
            : 'bg-white border-slate-200 hover:border-sky-300 hover:shadow'
        }
      `}
    >
      {/* Main content */}
      <div className="flex items-start gap-3 p-4">
        {/* Place image with placeholder fallback */}
        {imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={loc.name}
            loading="lazy"
            className="w-[72px] h-[72px] rounded-lg object-cover flex-shrink-0"
            onError={() => setImageError(true)}
          />
        ) : (
          <ImagePlaceholder />
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {MEAL_PATTERN.test(loc.name) ? (
              <FaUtensils className="text-orange-500 flex-shrink-0" />
            ) : loc.isAccommodation ? (
              <FaBed className="text-purple-500 flex-shrink-0" />
            ) : null}
            <p className={`font-semibold ${isVisited ? 'line-through text-slate-400' : 'text-slate-800'}`}>
              {loc.name}
            </p>
            {loc.starRating && (
              <span className="flex items-center gap-0.5 text-amber-500">
                {[...Array(loc.starRating)].map((_, i) => (
                  <FaStar key={i} size={10} />
                ))}
              </span>
            )}
          </div>
          {loc.lat && loc.lng && (
            <p className="text-xs text-slate-400 font-mono mt-0.5">
              ({loc.lat.toFixed(4)}, {loc.lng.toFixed(4)})
            </p>
          )}
          {loc.description && !loc.description.toLowerCase().startsWith('travel') && !loc.description.toLowerCase().includes('more info') && (
            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed line-clamp-3">
              {loc.description.replace(/\[?More\s*Info\]?\s*\(?[^)]*\)?/gi, '').trim()}
            </p>
          )}
          {/* The parser's flag alone — see the note in DayCard: requiring a
              booking link here too made the web disagree with mobile. */}
          {loc.isAccommodation && (
            <div className="mt-2">
              <BookingOptions
                type="hotel"
                hotelName={loc.name}
                city={destination}
                checkIn={checkInDate}
                checkOut={checkOutDate}
                compact={true}
                surface={bookingSurface ? `${bookingSurface}_day${dayNumber}` : undefined}
              />
            </div>
          )}
          {customTime && (
            <p className="text-xs text-slate-500 flex items-center gap-1 mt-1.5">
              <FaClock className="text-[10px] text-indigo-400" />
              {customTime}
            </p>
          )}
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => {
              if (onAttractionStatusUpdate && loc.lat && loc.lng) {
                onAttractionStatusUpdate(
                  dayNumber,
                  loc.name,
                  { lat: loc.lat, lng: loc.lng },
                  { visited: !isVisited }
                );
              }
            }}
            className={`p-2 rounded-lg transition-colors ${
              isVisited
                ? 'text-emerald-500 hover:bg-emerald-100'
                : 'text-slate-400 hover:text-emerald-500 hover:bg-slate-100'
            }`}
            title={isVisited ? 'Mark as not visited' : 'Mark as visited'}
          >
            {isVisited ? <FaCheckCircle className="text-base" /> : <FaRegCircle className="text-base" />}
          </button>
          {/* Lock button - only visible to owner */}
          {isOwner && loc.lat && loc.lng && (
            <button
              onClick={() => {
                if (onAttractionStatusUpdate) {
                  onAttractionStatusUpdate(
                    dayNumber,
                    loc.name,
                    { lat: loc.lat, lng: loc.lng },
                    { locked: !isLocked }
                  );
                }
              }}
              className={`p-2 rounded-lg transition-colors ${
                isLocked
                  ? 'text-amber-500 hover:bg-amber-100'
                  : 'text-slate-400 hover:text-amber-500 hover:bg-slate-100'
              }`}
              title={isLocked ? 'Unlock (allow optimization)' : 'Lock (prevent optimization)'}
            >
              {isLocked ? <FaLock className="text-base" /> : <FaLockOpen className="text-base" />}
            </button>
          )}
        </div>
      </div>

      {/* Action buttons row */}
      <div className="flex items-center border-t border-slate-100 bg-slate-50/50">
        <a
          href={getMoreInfoUrl(loc.name)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors border-r border-slate-100"
        >
          <FaInfoCircle />
          More Info
        </a>
        <a
          href={getPlaceUrl(loc)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-sky-600 hover:bg-sky-50 transition-colors border-r border-slate-100"
        >
          <FaMapMarkerAlt />
          View on Map
        </a>
        {loc.lat && loc.lng && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-teal-600 hover:bg-teal-50 transition-colors border-r border-slate-100"
          >
            <FaDirections />
            Directions
          </a>
        )}
        <div className="flex-1 flex items-center justify-center py-2.5">
          <ReviewLinks
            name={loc.name}
            location={destination}
            coordinates={loc.lat && loc.lng ? { lat: loc.lat, lng: loc.lng } : null}
          />
        </div>
        {isOwner && onRemoveStop && (
          <button
            onClick={handleRemove}
            disabled={removeState !== 'idle'}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors border-l border-slate-100 ${
              removeState === 'queued'
                ? 'bg-amber-50 text-amber-600 cursor-default'
                : removeState === 'removing'
                ? 'bg-slate-50 text-slate-400 cursor-wait'
                : 'text-red-500 hover:bg-red-50'
            }`}
          >
            {removeState === 'removing' ? (
              <>
                <span className="animate-spin">⏳</span>
                Removing...
              </>
            ) : removeState === 'queued' ? (
              <>
                <FaCheckCircle />
                Queued
              </>
            ) : (
              <>
                <FaTrash />
                Remove
              </>
            )}
          </button>
        )}
      </div>

      {/* Queued for removal banner */}
      {removeState === 'queued' && (
        <div className="bg-amber-50 border-t border-amber-200 px-4 py-2 flex items-center gap-2">
          <FaCheckCircle className="text-amber-500 flex-shrink-0" />
          <p className="text-xs text-amber-700">
            <span className="font-semibold">Queued for removal.</span> Apply pending changes to update your itinerary.
          </p>
        </div>
      )}

      {/* Explore stops along the way */}
      {onExplore && loc.lat && loc.lng && idx < locations.length - 1 && locations[idx + 1]?.lat && locations[idx + 1]?.lng && (
        <div className="border-t border-slate-100 bg-gradient-to-r from-emerald-50 to-teal-50">
          <button
            onClick={() => {
              onClose();
              onExplore(
                loc.name,
                locations[idx + 1].name,
                { lat: loc.lat, lng: loc.lng },
                { lat: locations[idx + 1].lat, lng: locations[idx + 1].lng }
              );
            }}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
          >
            <FaCompass className="text-emerald-500" />
            Explore stops along the way to {locations[idx + 1].name.length > 25 ? locations[idx + 1].name.substring(0, 25) + '...' : locations[idx + 1].name}
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * DayDetailModal - Slide-over modal with warm, colorful design.
 */
const DayDetailModal = ({
  isOpen,
  onClose,
  day,
  startDate,
  weatherData,
  attractionStatuses,
  onAttractionStatusUpdate,
  flights,
  isOwner,
  destination,
  onExplore,
  onRemoveStop,
  bookingSurface,
}) => {
  const voiceSupport = useVoiceSupport();

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Generate text for reading aloud (must be before early return to satisfy React hooks rules)
  const dayReadAloudText = useMemo(() => {
    if (!day) return '';
    const { day: dayNum, dayTitle, locations: locs = [] } = day;
    const dateStr = getDayDate(startDate, dayNum);
    const fDate = formatDayDate(dateStr);
    const locName = dayTitle?.includes(':')
      ? dayTitle.split(':').slice(1).join(':').trim()
      : dayTitle || `Day ${dayNum}`;
    const dayWeather = getDayWeather(dayNum, weatherData);
    const stops = locs.length;

    const parts = [`Day ${dayNum}, ${fDate}. ${locName}.`];
    if (stops > 0) {
      parts.push(`You have ${stops} stops planned:`);
      locs.forEach((loc, idx) => {
        parts.push(`Stop ${idx + 1}: ${loc.name}.`);
        if (loc.description) {
          // Clean up any markdown from description
          const cleanDesc = loc.description.replace(/\*\*/g, '').replace(/\n/g, ' ');
          parts.push(cleanDesc);
        }
      });
    }
    if (dayWeather && !dayWeather.skip_reason) {
      parts.push(`Weather: ${dayWeather.description || ''}, high of ${dayWeather.temp_max_f ? Math.round(dayWeather.temp_max_f) : '--'} degrees Fahrenheit.`);
    }
    return parts.join(' ');
  }, [day, startDate, weatherData]);

  if (!isOpen || !day) return null;

  const { day: dayNumber, dayTitle, locations = [] } = day;
  const dateStr = getDayDate(startDate, dayNumber);
  const formattedDate = formatDayDate(dateStr);
  // The night this day covers. Computed once, for every stay surface in the
  // modal: it used to be derived per stop and gated on the stop having a
  // booking link, so a flagged hotel without one opened a dateless search.
  const nightWindow = nightWindowForDay(startDate, dayNumber);
  const weather = getDayWeather(dayNumber, weatherData);

  // Extract location name from dayTitle
  const locationName = dayTitle?.includes(':')
    ? dayTitle.split(':').slice(1).join(':').trim()
    : dayTitle || `Day ${dayNumber}`;

  // Calculate stats
  let visitedCount = 0;
  locations.forEach((loc) => {
    const status = getAttractionStatus(loc, dayNumber, attractionStatuses);
    if (status === 'visited') visitedCount++;
  });
  const totalStops = locations.length;
  const completionPercent = totalStops > 0 ? Math.round((visitedCount / totalStops) * 100) : 0;

  // Get full route URL (encodes waypoints and caps them to Google's limit)
  const getFullRouteUrl = () => buildGoogleMapsRouteUrl(locations, { locality: destination });

  // Get search URL for more info about a location
  const getMoreInfoUrl = (locName) => {
    const searchQuery = destination
      ? `${locName} ${destination}`
      : locName;
    return `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
  };

  // Get Google Maps place URL
  const getPlaceUrl = (loc) => {
    if (loc.lat && loc.lng) {
      return `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.name)}`;
  };

  const fullRouteUrl = getFullRouteUrl();

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Slide-over Panel */}
      <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col h-full">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-sky-50 to-indigo-50 border-b border-sky-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-slate-600 hover:text-indigo-600 transition-colors"
            >
              <FaArrowLeft className="text-sm" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <div className="flex items-center gap-1">
              <PageGuide pageKey="view-day" />
              <button
                onClick={onClose}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white/60 rounded-lg transition-colors"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2.5 py-1 bg-sky-500 text-white rounded-lg text-xs font-bold shadow-sm">
                  Day {dayNumber}
                </span>
                <span className="text-sm text-slate-500 font-medium">{formattedDate}</span>
              </div>
              <h2 className="text-xl font-bold text-slate-800">{locationName}</h2>

              {/* Progress */}
              {totalStops > 0 && (
                <div className="flex items-center gap-3 mt-3">
                  <div className="w-28 h-2 bg-white rounded-full overflow-hidden shadow-inner">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 transition-all"
                      style={{ width: `${completionPercent}%` }}
                    />
                  </div>
                  <span className="text-xs font-bold text-emerald-600">{visitedCount}/{totalStops} completed</span>
                </div>
              )}

              {/* Read Aloud Button */}
              {voiceSupport.speechSynthesis && (
                <div className="mt-3">
                  <ReadAloudButton
                    text={dayReadAloudText}
                    buttonText="Read Day"
                    speakingText="Stop"
                    className="text-xs px-3 py-1.5"
                  />
                </div>
              )}
            </div>

            {/* Weather */}
            {weather && !weather.skip_reason && (
              <div className="text-center bg-white border border-sky-200 rounded-xl px-4 py-2 shadow-sm">
                <span className="text-3xl block">{weather.emoji}</span>
                <p className="text-sm font-bold text-slate-700">
                  {weather.temp_max_f ? `${Math.round(weather.temp_max_f)}°F` : '--'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-slate-50">
          {/* Weather (top of day details) */}
          {weather && !weather.skip_reason && (
            <div className="px-4 pt-4">
              <DayWeather
                dayNumber={dayNumber}
                weatherData={weather}
                loading={false}
                error={null}
              />
            </div>
          )}

          {/* Quick Actions */}
          {fullRouteUrl && (
            <div className="p-4 bg-white border-b border-slate-100">
              <a
                href={fullRouteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-700 hover:to-sky-700 text-white font-semibold rounded-xl transition-all shadow-sm hover:shadow"
              >
                <FaRoute />
                Open Full Route in Google Maps
              </a>
            </div>
          )}

          {/* Stops Section */}
          <div className="p-4">
            <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
              <FaMapMarkerAlt className="text-indigo-500" />
              Stops ({totalStops})
            </h3>

            {locations.length > 0 ? (
              <div className="space-y-3">
                {locations.map((loc, idx) => {
                  const attractionKey = getAttractionKey(loc.name, loc.lat, loc.lng, dayNumber);
                  const status = attractionStatuses[attractionKey];
                  const isVisited = status?.visited || false;
                  const isLocked = status?.locked || false;
                  const customTime = status?.custom_time || '';

                  return (
                    <DayStopCard
                      key={idx}
                      loc={loc}
                      idx={idx}
                      dayNumber={dayNumber}
                      isVisited={isVisited}
                      isLocked={isLocked}
                      customTime={customTime}
                      onAttractionStatusUpdate={onAttractionStatusUpdate}
                      destination={locationName || destination}
                      getMoreInfoUrl={getMoreInfoUrl}
                      getPlaceUrl={getPlaceUrl}
                      locations={locations}
                      onExplore={onExplore}
                      onClose={onClose}
                      checkInDate={nightWindow.checkIn}
                      checkOutDate={nightWindow.checkOut}
                      onRemoveStop={onRemoveStop}
                      isOwner={isOwner}
                      bookingSurface={bookingSurface}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10 bg-white rounded-xl border-2 border-dashed border-slate-200">
                <FaMapMarkerAlt className="text-3xl text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 font-medium">No stops planned for this day</p>
              </div>
            )}
          </div>

          {/* Tip for users */}
          <div className="px-4 pb-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs text-amber-800">
                <span className="font-semibold">Tip:</span> Click "More Info" to learn about hours, reviews, and tips for each stop. Use "Detailed" view in the itinerary for full descriptions.
              </p>
            </div>
            {/* Where to stay on Day N — fallback for a day the AI never named
                anywhere to sleep, so no day is left without a stay surface. */}
            {destination && !dayHasAccommodation(locations) && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-xs font-medium text-slate-500 mb-2">
                  Where to stay on Day {dayNumber}
                </p>
                <BookingOptions
                  type="hotel"
                  city={destination}
                  checkIn={nightWindow.checkIn}
                  checkOut={nightWindow.checkOut}
                  compact={true}
                  surface={bookingSurface ? `${bookingSurface}_day${dayNumber}_stay` : undefined}
                />
              </div>
            )}
            <AffiliateDisclosure className="mt-3" />
          </div>

        </div>
      </div>
    </div>
  );
};

export default DayDetailModal;
